import { useEffect, useState } from "react";
// import axios from "axios";
// import { API_URL } from "../config/api";
import {
  FaHistory,
  FaBrain,
  FaMap,
  FaCalendar,
  FaClock,
  FaChartLine,
  FaUserTie,
  FaTrashAlt,
  FaInfoCircle
} from "react-icons/fa";
import { HiAcademicCap, HiSparkles } from "react-icons/hi";
import { Link } from 'react-router-dom';

import { getUserHistory, clearUserHistory } from "../services/userService";
// ... other imports

const History = () => {
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [roadmapHistory, setRoadmapHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const data = await getUserHistory();
        setAnalysisHistory(data.analysisHistory || []);
        setRoadmapHistory(data.roadmapHistory || []);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load history");
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading your activity history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md w-full bg-white p-8 rounded-3xl shadow-xl border border-red-100">
          <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center text-red-500 mx-auto mb-6">
            <FaHistory className="w-10 h-10" />
          </div>
                  <h2 className="heading-tertiary text-gray-900">
            Error Loading History
          </h2>
          <p className="text-gray-600 mb-8">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="w-full py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition-colors shadow-lg shadow-red-500/30"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header Section */}
        <div className="text-center mb-16 animate-fade-in-down">
          <div className="inline-flex items-center px-4 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-bold uppercase tracking-wider mb-6">
            <FaHistory className="w-4 h-4 mr-2" />
            Activity History
          </div>
          
          <h1 className="heading-primary mb-6 text-gray-900">
            Your Learning <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Journey</span>
          </h1>
          
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            Track your progress, revisit your analyses, and celebrate your milestones.
          </p>

            {(analysisHistory.length > 0 || roadmapHistory.length > 0) && (
             <button
            className="inline-flex items-center px-6 py-2.5 bg-red-50 text-red-600 hover:bg-red-100 font-semibold rounded-xl transition-colors border border-red-100 group"
            onClick={async () => {
              if (window.confirm('Are you sure you want to clear all your history? This action cannot be undone.')) {
                try {
                  await clearUserHistory();
                  setAnalysisHistory([]);
                  setRoadmapHistory([]);
                } catch (err) {
                  alert('Failed to clear history. Please try again.');
                }
              }
            }}
          >
            <FaTrashAlt className="mr-2 group-hover:scale-110 transition-transform" />
            Clear All History
          </button>
            )}
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16 animate-fade-in-up">
             {[
                { label: "Skill Analyses", value: analysisHistory.length, icon: FaBrain, color: "from-blue-500 to-cyan-500" },
                { label: "Roadmaps", value: roadmapHistory.length, icon: FaMap, color: "from-purple-500 to-pink-500" },
                { 
                    label: "Skills Tracked", 
                    value: analysisHistory.reduce((total, item) => total + item.skills.length, 0), 
                    icon: FaChartLine, 
                    color: "from-amber-500 to-orange-500" 
                },
                { 
                    label: "Active Days", 
                    value: Math.max(analysisHistory.length, roadmapHistory.length), 
                    icon: FaCalendar, 
                    color: "from-emerald-500 to-green-500" 
                }
            ].map((stat, idx) => (
                <div key={idx} className="bg-white p-6 rounded-3xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
                    <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${stat.color} flex items-center justify-center text-white text-xl mb-4 shadow-md`}>
                        <stat.icon />
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</h3>
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">{stat.label}</p>
                </div>
            ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Analysis History */}
          <section className="animate-slide-in-left">
            <div className="bg-white p-8 rounded-[2.5rem] shadow-xl border border-gray-100 h-full">
              <div className="flex items-center space-x-4 mb-8">
                <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center text-2xl shadow-sm">
                  <FaBrain />
                </div>
                <div>
                  <h2 className="heading-tertiary text-gray-900">
                    Recent Analyses
                  </h2>
                  <p className="text-gray-500 font-medium">
                    Your skill assessment timeline
                  </p>
                </div>
              </div>

              {analysisHistory.length === 0 ? (
                <div className="text-center py-16 bg-gray-50 rounded-3xl border border-dashed border-gray-200">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm text-gray-300">
                    <HiSparkles className="w-8 h-8" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    No analyses yet
                  </h3>
                  <p className="text-gray-500 mb-6 max-w-xs mx-auto">
                    Discover your strengths and gaps with our AI-powered assessment.
                  </p>
                  <Link to="/skills-analysis" className="inline-block px-6 py-2.5 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-colors shadow-lg shadow-blue-500/20">
                    Analyze Skills
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {analysisHistory
                    .slice()
                    .reverse()
                    .map((item, idx) => (
                      <div
                        key={idx}
                        className="group p-5 bg-gray-50 hover:bg-white border boundary-transparent hover:border-blue-100 rounded-2xl transition-all duration-300 hover:shadow-lg cursor-default"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className="bg-white p-2 rounded-lg shadow-sm text-blue-600">
                                <FaUserTie />
                            </div>
                            <h3 className="font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                              {item.targetRole}
                            </h3>
                          </div>
                          <span className="text-xs font-semibold text-gray-500 bg-white px-3 py-1 rounded-full border border-gray-100 shadow-sm">
                            {formatDate(item.createdAt)}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-4 text-xs font-medium text-gray-500 mb-4 px-1">
                            <span className="flex items-center gap-1.5"><FaBrain className="text-blue-400"/> {item.skills.length} Skills</span>
                            <span className="flex items-center gap-1.5"><FaClock className="text-blue-400"/> Completed</span>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {item.skills.slice(0, 3).map((skill, skillIdx) => (
                            <span key={skillIdx} className="px-2.5 py-1 bg-white border border-gray-100 rounded-lg text-xs font-semibold text-gray-700 shadow-sm">
                              {skill.name}
                            </span>
                          ))}
                          {item.skills.length > 3 && (
                            <span className="px-2.5 py-1 bg-gray-100 rounded-lg text-xs font-semibold text-gray-500">
                              +{item.skills.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </section>

          {/* Roadmap History */}
          <section className="animate-slide-in-right">
            <div className="bg-white p-8 rounded-[2.5rem] shadow-xl border border-gray-100 h-full">
              <div className="flex items-center space-x-4 mb-8">
                <div className="w-14 h-14 bg-purple-50 text-purple-600 rounded-2xl flex items-center justify-center text-2xl shadow-sm">
                  <FaMap />
                </div>
                <div>
                  <h2 className="heading-tertiary text-gray-900">
                    Generated Roadmaps
                  </h2>
                  <p className="text-gray-500 font-medium">
                    Your career path blueprints
                  </p>
                </div>
              </div>

              {roadmapHistory.length === 0 ? (
                <div className="text-center py-16 bg-gray-50 rounded-3xl border border-dashed border-gray-200">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm text-gray-300">
                    <HiAcademicCap className="w-8 h-8" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    No roadmaps yet
                  </h3>
                  <p className="text-gray-500 mb-6 max-w-xs mx-auto">
                    Create a personalized learning path to reach your goals.
                  </p>
                  <Link to="/roadmap" className="inline-block px-6 py-2.5 bg-purple-600 text-white font-bold rounded-xl hover:bg-purple-700 transition-colors shadow-lg shadow-purple-500/20">
                    Create Roadmap
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {roadmapHistory
                    .slice()
                    .reverse()
                    .map((item, idx) => (
                      <div
                        key={idx}
                        className="group p-5 bg-gray-50 hover:bg-white border boundary-transparent hover:border-purple-100 rounded-2xl transition-all duration-300 hover:shadow-lg"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className="bg-white p-2 rounded-lg shadow-sm text-purple-600">
                                <HiAcademicCap />
                            </div>
                            <h3 className="font-bold text-gray-900 group-hover:text-purple-600 transition-colors">
                              {item.topic}
                            </h3>
                          </div>
                          <span className="text-xs font-semibold text-gray-500 bg-white px-3 py-1 rounded-full border border-gray-100 shadow-sm">
                            {formatDate(item.createdAt)}
                          </span>
                        </div>

                        <div className="flex items-center gap-4 text-xs font-medium text-gray-500 mb-4 px-1">
                            <span className="flex items-center gap-1.5"><FaCalendar className="text-purple-400"/> {item.duration} Months</span>
                            <span className="flex items-center gap-1.5"><FaClock className="text-purple-400"/> {item.level}</span>
                        </div>

                        <div className="flex items-center justify-between pt-2 border-t border-gray-200/50">
                          <span className="text-xs font-medium text-gray-400 flex items-center gap-1">
                            <FaInfoCircle /> Auto-generated path
                          </span>
                          <Link
                            to="/roadmap"
                            className="text-xs font-bold text-purple-600 hover:text-purple-700 flex items-center gap-1 group/link"
                          >
                            View Details <span className="transform group-hover/link:translate-x-0.5 transition-transform">→</span>
                          </Link>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default History;
